import React from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Slide,
  CircularProgress,
  IconButton,
  Icon,
  MenuItem,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SwitchToggle from "components/SwitchToggle/SwitchToggle";
import { userService } from "_services/user.service";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";
import { PER_PAGE_OPTIONS, DEFAULT_PER_PAGE_RECORDS } from "__helpers/constants";
import { baseRoutes } from "base-routes";

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="down" ref={ref} {...props} />
));

const MAX_NAME_LENGTH = 50; // master_insurance.insurance_name is varchar(50)

// Sentinel understood by GET /insurances (CustomMasterInsuranceRepository.UNTYPED): filter to
// rows whose insurance_type is NULL. Needed because every insurance that predates the type
// column is untyped, and this filter is how the admin works through that backlog.
const UNTYPED_FILTER = -1;

/**
 * Admin CRUD for master_insurance.
 *
 * There is no delete. `activated` is the soft delete: insurance_id is referenced by
 * jhi_patient (and the same column in external_patient / planner / provider), so a row can
 * never actually be removed without orphaning those patients. Deactivating drops it out of
 * /insuranceList — which is what feeds the patient and provider forms — and out of
 * InsuranceMatchService's PCC payer matching, while every existing reference stays valid.
 *
 * This screen reads /insurances, NOT /insuranceList: the latter is activated-only, so a
 * deactivated insurance would vanish from the very screen that has to switch it back on.
 */
export default class Insurance extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      insurances: [],
      total: 0,
      currentPage: 0,
      perPage: DEFAULT_PER_PAGE_RECORDS,
      search: "",
      typeFilter: "", // "" = all, UNTYPED_FILTER = no type, otherwise an insuranceTypeId
      loading: false,

      // Read-only catalogue from /insurance-types. Loaded once and used three ways: the
      // dropdown in the dialog, the filter above the table, and the id -> name lookup for the
      // Insurance Type column (the listing rows carry only the id).
      insuranceTypes: [],

      modalStatus: false,
      saving: false,
      editingId: null, // null = create, otherwise the row being renamed
      insuranceName: "",
      insuranceType: "", // "" = no type; the column is nullable and stays optional
      formError: "",

      togglingIds: [],
    };
    this.searchDebounce = null;
  }

  componentDidMount() {
    this.fetchInsuranceTypes();
    this.fetchInsurances();
  }

  componentWillUnmount() {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  }

  fetchInsuranceTypes = async () => {
    try {
      const response = await fetch(apiPath.insuranceTypes, {
        method: "GET",
        headers: generalAuthTokenHeader(),
      });
      if (response.status === 401) {
        userService.logout();
        window.location.replace(baseRoutes.login.path);
        return;
      }
      if (!response.ok) {
        throw new Error("load failed");
      }
      const data = await response.json();
      this.setState({ insuranceTypes: data || [] });
    } catch (err) {
      // Non-fatal: the list still renders, the type column just falls back to "—" and the
      // dropdown is empty. Saving a name still works.
      userService.showNotification({
        title: "Insurance",
        message: "Could not load the insurance types.",
        type: "danger",
      });
    }
  };

  /** master_insurance rows carry only insurance_type; the name comes from the catalogue. */
  typeName = (insuranceTypeId) => {
    if (!insuranceTypeId) {
      return "—";
    }
    const match = this.state.insuranceTypes.find(
      (t) => t.insuranceTypeId === insuranceTypeId
    );
    return match ? match.insuranceTypeName : "—";
  };

  fetchInsurances = async () => {
    this.setState({ loading: true });
    try {
      const { currentPage, perPage, search, typeFilter } = this.state;
      let url =
        apiPath.insurances +
        "?page=" +
        currentPage +
        "&size=" +
        perPage +
        "&sort=insuranceName,asc";
      if (search && search.trim()) {
        url += "&search=" + encodeURIComponent(search.trim());
      }
      if (typeFilter !== "") {
        url += "&insuranceType=" + typeFilter;
      }
      const response = await fetch(url, {
        method: "GET",
        headers: generalAuthTokenHeader(),
      });
      if (response.status === 401) {
        userService.logout();
        window.location.replace(baseRoutes.login.path);
        return;
      }
      if (!response.ok) {
        throw new Error("load failed");
      }
      const total = response.headers.get("X-Total-Count");
      const data = await response.json();
      this.setState({ insurances: data || [], total: parseInt(total || 0, 10) });
    } catch (err) {
      userService.showNotification({
        title: "Insurance",
        message: "Could not load the insurance list. Please try again.",
        type: "danger",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  // Server-side search, debounced — the list is paged, so filtering client-side would only
  // ever search the page currently on screen.
  handleSearch = (event) => {
    const value = event.target.value;
    this.setState({ search: value, currentPage: 0 });
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    this.searchDebounce = setTimeout(() => this.fetchInsurances(), 400);
  };

  // Filtered server-side for the same reason search is: the listing is paged, so filtering
  // client-side would only ever filter the page on screen.
  handleTypeFilter = (event) => {
    this.setState({ typeFilter: event.target.value, currentPage: 0 }, () =>
      this.fetchInsurances()
    );
  };

  handleChangePage = (event, newPage) => {
    this.setState({ currentPage: newPage }, () => this.fetchInsurances());
  };

  handleChangeRowsPerPage = (event) => {
    this.setState(
      { perPage: parseInt(event.target.value, 10), currentPage: 0 },
      () => this.fetchInsurances()
    );
  };

  modalOpen = () => {
    this.setState({
      modalStatus: true,
      editingId: null,
      insuranceName: "",
      insuranceType: "",
      formError: "",
    });
  };

  openEdit = (row) => {
    this.setState({
      modalStatus: true,
      editingId: row.insuranceId,
      insuranceName: row.insuranceName || "",
      insuranceType: row.insuranceType || "",
      formError: "",
    });
  };

  modalClose = () => {
    this.setState({ modalStatus: false, formError: "", saving: false });
  };

  validate = () => {
    const name = (this.state.insuranceName || "").trim();
    if (!name) {
      this.setState({ formError: "Insurance name is required." });
      return null;
    }
    if (name.length > MAX_NAME_LENGTH) {
      this.setState({
        formError: "Insurance name must be " + MAX_NAME_LENGTH + " characters or fewer.",
      });
      return null;
    }
    return name;
  };

  saveInsurance = async () => {
    const name = this.validate();
    if (!name) {
      return;
    }
    const { editingId, insuranceType } = this.state;
    // "" -> null: "No Type" is a real, allowed value, not a missing field.
    const typeId = insuranceType === "" ? null : Number(insuranceType);
    this.setState({ saving: true, formError: "" });
    try {
      const response = await fetch(apiPath.insurance, {
        method: editingId ? "PUT" : "POST",
        headers: generalAuthTokenHeader(),
        body: JSON.stringify(
          editingId
            ? { insuranceId: editingId, insuranceName: name, insuranceType: typeId }
            : { insuranceName: name, insuranceType: typeId, activated: true }
        ),
      });
      if (response.status === 401) {
        userService.logout();
        window.location.replace(baseRoutes.login.path);
        return;
      }
      if (!response.ok) {
        // Server-side failures — the duplicate-name 400 chief among them — go to an error
        // toast. The inline formError is kept for client-side validation only (required,
        // length), where the message belongs next to the field being corrected.
        let message = "Could not save this insurance. Please try again.";
        try {
          const body = await response.json();
          if (body && body.title) {
            message = body.title;
          }
        } catch (parseErr) {
          /* keep the default message */
        }
        userService.showNotification({
          title: "Insurance",
          message: message,
          type: "danger",
        });
        this.setState({ saving: false });
        return;
      }
      userService.showNotification({
        title: "Insurance",
        message: editingId ? "Insurance updated." : "Insurance added.",
        type: "success",
      });
      this.setState({ modalStatus: false, saving: false }, () => this.fetchInsurances());
    } catch (err) {
      userService.showNotification({
        title: "Insurance",
        message: "Could not save this insurance. Please try again.",
        type: "danger",
      });
      this.setState({ saving: false });
    }
  };

  /**
   * Active / inactive. Hits the dedicated status endpoint rather than PUT /insurance so the
   * name is never part of the payload and cannot be blanked by an incomplete update.
   */
  toggleStatus = async (row) => {
    const id = row.insuranceId;
    const next = !(row.activated !== false);
    this.setState((prev) => ({ togglingIds: prev.togglingIds.concat(id) }));
    try {
      const response = await fetch(
        apiPath.insurance + "/" + id + "/status?activated=" + next,
        { method: "PUT", headers: generalAuthTokenHeader() }
      );
      if (response.status === 401) {
        userService.logout();
        window.location.replace(baseRoutes.login.path);
        return;
      }
      if (!response.ok) {
        throw new Error("save failed");
      }
      // Update in place — the row stays in this listing either way, so a refetch would only
      // cost the admin their scroll position and page.
      this.setState((prev) => ({
        insurances: prev.insurances.map((i) =>
          i.insuranceId === id ? { ...i, activated: next } : i
        ),
      }));
    } catch (err) {
      userService.showNotification({
        title: "Insurance",
        message: "Could not change the status. Please try again.",
        type: "danger",
      });
    } finally {
      this.setState((prev) => ({
        togglingIds: prev.togglingIds.filter((t) => t !== id),
      }));
    }
  };

  render() {
    const {
      insurances,
      loading,
      editingId,
      insuranceName,
      insuranceType,
      insuranceTypes,
      formError,
    } = this.state;

    return (
      <div>
        <GridContainer>
          <Card className={"advert-cover"}>
            <GridItem xs={12} sm={12} md={12}>
              <CardHeader color="success" className={"cusCardheader cusCardTitle"}>
                <h4>Insurance</h4>
              </CardHeader>
            </GridItem>

            <div className={"custom-container"}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                  <div className="search-btn-cover" style={{ float: "none" }}>
                    <input
                      type="text"
                      placeholder="Search insurance by name."
                      className="search-btn"
                      value={this.state.search}
                      onChange={this.handleSearch}
                    />
                    <i className="material-icons">search</i>
                  </div>
                  {/*
                    No floating label on purpose. A labelled TextField reserves a label row
                    above the input, so centring it against .search-btn-cover (which is just an
                    input in 25px of padding) leaves the visible box sitting ~8px low. Without
                    the label the control is the input row alone and the two line up. The empty
                    option carries the wording the label would have.
                  */}
                  <TextField
                    select
                    value={this.state.typeFilter}
                    onChange={this.handleTypeFilter}
                    style={{ minWidth: 200, marginLeft: 16 }}
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: {
                        PaperProps: { style: { maxHeight: 300 } },
                        variant: "menu",
                        getContentAnchorEl: null,
                      },
                    }}
                  >
                    <MenuItem value="">All Insurance Types</MenuItem>
                    <MenuItem value={UNTYPED_FILTER}>No Type</MenuItem>
                    {insuranceTypes.map((t) => (
                      <MenuItem key={t.insuranceTypeId} value={t.insuranceTypeId}>
                        {t.insuranceTypeName}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
                <div className="button-cover" style={{ float: "none" }}>
                  <button className="pointer btn1" onClick={this.modalOpen}>
                    Add New Insurance
                  </button>
                </div>
              </div>

              <div className="tableResponsive" style={{ clear: "both" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Insurance Name</TableCell>
                      <TableCell>Insurance Type</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {insurances && insurances.length ? (
                      insurances.map((row) => (
                        <TableRow key={row.insuranceId}>
                          <TableCell>
                            <strong>{row.insuranceName}</strong>
                          </TableCell>
                          <TableCell>{this.typeName(row.insuranceType)}</TableCell>
                          <TableCell align="center">
                            <div style={{ display: "flex", justifyContent: "center" }}>
                              <SwitchToggle
                                id={row.insuranceId}
                                status={row.activated !== false}
                                disabled={this.state.togglingIds.includes(row.insuranceId)}
                                clickHandler={() => this.toggleStatus(row)}
                                ariaLabel={
                                  (row.activated !== false ? "Deactivate " : "Activate ") +
                                  row.insuranceName
                                }
                              />
                            </div>
                          </TableCell>
                          <TableCell style={{ whiteSpace: "nowrap" }}>
                            <span
                              className="edit-action"
                              title="Edit insurance"
                              onClick={() => this.openEdit(row)}
                            >
                              <Icon className="fa fa-pencil" aria-hidden="true" />
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan="4">
                          <div className="not-found-text">
                            {loading ? "Loading..." : "No insurance found."}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <TablePagination
                rowsPerPageOptions={PER_PAGE_OPTIONS}
                component="div"
                count={parseInt(this.state.total)}
                rowsPerPage={this.state.perPage}
                page={this.state.currentPage}
                onChangePage={this.handleChangePage}
                onChangeRowsPerPage={this.handleChangeRowsPerPage}
              />
            </div>
          </Card>
        </GridContainer>

        <Dialog
          open={this.state.modalStatus}
          TransitionComponent={Transition}
          keepMounted
          onClose={this.modalClose}
          className="front-modal add-modal"
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle className="add-modal-title">
            {editingId ? "Edit Insurance" : "Add New Insurance"}
            <IconButton className="closeButton" aria-label="close" onClick={this.modalClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Insurance Name"
              value={insuranceName}
              inputProps={{ maxLength: MAX_NAME_LENGTH }}
              onChange={(e) =>
                this.setState({ insuranceName: e.target.value, formError: "" })
              }
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  this.saveInsurance();
                }
              }}
            />
            <TextField
              select
              fullWidth
              label="Insurance Type"
              value={insuranceType}
              onChange={(e) => this.setState({ insuranceType: e.target.value })}
              style={{ marginTop: 16 }}
              helperText="Optional"
              SelectProps={{
                MenuProps: {
                  PaperProps: { style: { maxHeight: 300 } },
                  variant: "menu",
                  getContentAnchorEl: null,
                },
              }}
            >
              <MenuItem value="">No Type</MenuItem>
              {insuranceTypes.map((t) => (
                <MenuItem key={t.insuranceTypeId} value={t.insuranceTypeId}>
                  {t.insuranceTypeName}
                </MenuItem>
              ))}
            </TextField>
            {formError ? (
              <div className="error" style={{ marginTop: 8 }}>
                {formError}
              </div>
            ) : null}
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button type="button" className="btn2" onClick={this.modalClose}>
              Cancel
            </Button>
            <Button
              type="button"
              className="btn1"
              onClick={this.saveInsurance}
              disabled={this.state.saving}
            >
              {this.state.saving ? (
                <CircularProgress size={18} />
              ) : editingId ? (
                "Update"
              ) : (
                "Save"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
